import { Box, BoxProps } from '@mui/material'
import {
  ILLUSTRATIONS,
  ILLUSTRATION_SIZES,
  IllustrationName,
  IllustrationSize,
} from 'src/design-system/illustrations.generated'

// ----------------------------------------------------------------------
// The illustration tier. Art, not an icon.
//
// A separate pack from the interface glyphs, filled rather than stroked, so
// it cannot share their weight and must not be used where a glyph belongs:
// never inside a control, never beside body text, never standing in for a
// verb. It is for the places where art leads and there is nothing to
// operate. An entry screen, an empty state, a mode a provider cannot pick.
//
// The size is a named place rather than a number. The tier has a floor of
// 32, below which it reads as a badly drawn icon, and a prop that only takes
// steps cannot be handed a number under it.
//
// The path data is compiled in. Fetching or masking the art both fail the
// same way: the element paints its full background box, so the screen shows
// a solid rectangle where the car should be and nothing reports it.
// ----------------------------------------------------------------------

export interface IllusProps
  extends Omit<
    BoxProps,
    'children' | 'width' | 'height' | 'dangerouslySetInnerHTML'
  > {
  name: IllustrationName
  /** Where it sits, not how many pixels. See the tier table in the docs. */
  size?: IllustrationSize
  /**
   * What the art says, when it says something the surrounding copy does not.
   * Left off it is decorative and hidden, which is the right default: art
   * repeating the heading beside it is noise to a screen reader.
   */
  title?: string
}

export default function Illus({
  name,
  size = 'card',
  title,
  sx,
  // Discarded, not forwarded. Omitting them from the prop type is not enough
  // on its own: a spread through a wrapper is not excess-property checked, so
  // without this a caller could still hand over a width and defeat the floor
  // this component exists to hold, or hand over its own markup and replace
  // build-checked art with anything.
  width: _ignoredWidth,
  height: _ignoredHeight,
  dangerouslySetInnerHTML: _ignoredHtml,
  ...other
}: IllusProps & {
  width?: never
  height?: never
  dangerouslySetInnerHTML?: never
}) {
  const art = ILLUSTRATIONS[name]
  const side = ILLUSTRATION_SIZES[size]

  return (
    <Box
      component="svg"
      viewBox={art.viewBox}
      width={side}
      height={side}
      fill="currentColor"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
      sx={[
        { display: 'block', flex: '0 0 auto' },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      dangerouslySetInnerHTML={{ __html: art.body }}
      {...other}
    />
  )
}
